import React, { useEffect, useState, useMemo } from 'react';
import { CHAPTER_IDS, CHAPTER_TITLES_DE } from '@shared/chapters';
import { adminFetch } from '../lib/api';
import {
  HelpCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Image as ImageIcon,
  Edit,
  Eye,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  Save,
  RefreshCw,
  Bot,
  Sparkles,
  Globe,
  FileJson,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

const LANGUAGES = [
  { code: 'de', label: '🇩🇪 Deutsch', flag: '🇩🇪' },
  { code: 'en', label: '🇬🇧 English', flag: '🇬🇧' },
  { code: 'tr', label: '🇹🇷 Türkçe', flag: '🇹🇷' },
  { code: 'ar', label: '🇸🇦 العربية', flag: '🇸🇦' },
];

interface QuestionRow {
  id: string;
  chapter_id: number;
  difficulty: string;
  type: string;
  points: number;
  question_text_de: string;
  options_de: { id: string; text: string }[];
  explanation_de: string;
  question_text_en?: string;
  options_en?: { id: string; text: string }[];
  explanation_en?: string;
  question_text_tr?: string;
  options_tr?: { id: string; text: string }[];
  explanation_tr?: string;
  question_text_ar?: string;
  options_ar?: { id: string; text: string }[];
  explanation_ar?: string;
  correct_option_ids: string[];
  tags: string[];
  image_placeholder: string | null;
  image_url?: string | null;
  visual_prompt: string | null;
}

type SortKey = 'chapter_id' | 'difficulty' | 'type' | 'question_text_de' | 'points';
type SortDir = 'asc' | 'desc';

const CHAPTERS = CHAPTER_IDS;
const DIFFICULTIES = ['Leicht', 'Mittel', 'Schwer'];
const TYPES = ['Single Choice', 'Multiple Choice', 'Situation'];

export const QuestionsPage: React.FC = () => {
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChapter, setFilterChapter] = useState<number | ''>('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterHasImage, setFilterHasImage] = useState<'' | 'yes' | 'no'>('');
  const [sortKey, setSortKey] = useState<SortKey>('chapter_id');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingQuestion, setEditingQuestion] = useState<QuestionRow | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [viewLang, setViewLang] = useState('de');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      // Exclude image_placeholder to avoid transferring huge base64 data
      const { data, error } = await supabase
        .from('questions')
        .select(
          'id, chapter_id, difficulty, type, points, question_text_de, options_de, explanation_de, question_text_en, options_en, explanation_en, question_text_tr, options_tr, explanation_tr, question_text_ar, options_ar, explanation_ar, correct_option_ids, tags, visual_prompt, image_url'
        )
        .order('chapter_id', { ascending: true });

      if (error) throw error;

      // Lightweight check: which questions have images?
      let idsWithImage = new Set<string>();
      try {
        const { data: imgData } = await adminDb.rpc('get_questions_with_images');
        idsWithImage = new Set((imgData || []).map((r: any) => r.id));
      } catch {
        // RPC might not exist, fall back to no image info
      }

      const processed = (data || []).map((q) => ({
        ...q,
        image_placeholder: idsWithImage.has(q.id) ? 'has_image' : null,
      }));
      setQuestions(processed as any);
    } catch (err: any) {
      setMessage({ text: `Fehler beim Laden: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Compute per-chapter numbering for all questions
  const chapterQuestionNumbers = useMemo(() => {
    const map: Record<string, { num: number; total: number }> = {};
    const grouped: Record<number, string[]> = {};
    // Group question IDs by chapter (sorted by ID for stable ordering)
    const sorted = [...questions].sort((a, b) => a.id.localeCompare(b.id));
    sorted.forEach((q) => {
      if (!grouped[q.chapter_id]) grouped[q.chapter_id] = [];
      grouped[q.chapter_id].push(q.id);
    });
    // Assign numbers
    Object.entries(grouped).forEach(([, ids]) => {
      ids.forEach((id, index) => {
        map[id] = { num: index + 1, total: ids.length };
      });
    });
    return map;
  }, [questions]);

  const filtered = useMemo(() => {
    let result = [...questions];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((q) => {
        const textKey = `question_text_${viewLang}` as keyof QuestionRow;
        const text = (q[textKey] as string) || q.question_text_de || '';
        return text.toLowerCase().includes(term) || q.id.toLowerCase().includes(term);
      });
    }
    if (filterChapter !== '') result = result.filter((q) => q.chapter_id === filterChapter);
    if (filterDifficulty) result = result.filter((q) => q.difficulty === filterDifficulty);
    if (filterType) result = result.filter((q) => q.type === filterType);
    if (filterHasImage === 'yes') result = result.filter((q) => q.image_placeholder);
    if (filterHasImage === 'no') result = result.filter((q) => !q.image_placeholder);

    result.sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      // Secondary sort: by chapter number within chapter
      const aNum = chapterQuestionNumbers[a.id]?.num || 0;
      const bNum = chapterQuestionNumbers[b.id]?.num || 0;
      return aNum - bNum;
    });

    return result;
  }, [
    questions,
    searchTerm,
    filterChapter,
    filterDifficulty,
    filterType,
    filterHasImage,
    sortKey,
    sortDir,
    chapterQuestionNumbers,
  ]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((q) => q.id)));
    }
  };

  const handleDelete = async (ids: string[]) => {
    if (!confirm(`${ids.length} Frage(n) wirklich löschen?`)) return;

    try {
      const { error } = await adminDb.from('questions').delete().in('id', ids);
      if (error) throw error;
      setMessage({ text: `${ids.length} Frage(n) geloescht.`, type: 'success' });
      setSelectedIds(new Set());
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    }
  };

  const handleSaveQuestion = async (q: QuestionRow) => {
    setSaving(true);
    try {
      const { error } = await adminDb.from('questions').upsert({
        id: q.id,
        chapter_id: q.chapter_id,
        difficulty: q.difficulty,
        type: q.type,
        points: q.points,
        question_text_de: q.question_text_de,
        options_de: q.options_de,
        explanation_de: q.explanation_de,
        question_text_en: q.question_text_en || null,
        options_en: q.options_en || null,
        explanation_en: q.explanation_en || null,
        question_text_tr: q.question_text_tr || null,
        options_tr: q.options_tr || null,
        explanation_tr: q.explanation_tr || null,
        question_text_ar: q.question_text_ar || null,
        options_ar: q.options_ar || null,
        explanation_ar: q.explanation_ar || null,
        correct_option_ids: q.correct_option_ids,
        tags: q.tags,
        image_placeholder: q.image_placeholder,
        visual_prompt: q.visual_prompt,
      });
      if (error) throw error;
      setMessage({ text: 'Frage gespeichert.', type: 'success' });
      setEditingQuestion(null);
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Kapitel', 'Schwierigkeit', 'Typ', 'Punkte', 'Fragetext', 'Hat Bild'];
    const rows = filtered.map((q) => [
      q.id,
      q.chapter_id,
      q.difficulty,
      q.type,
      q.points,
      `"${q.question_text_de?.replace(/"/g, '""') || ''}"`,
      q.image_placeholder ? 'Ja' : 'Nein',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo_fragen_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  // ===== JSON Export (all languages, all fields) =====
  const handleExportJSON = () => {
    const exportData = filtered.map((q) => ({
      id: q.id,
      chapter_id: q.chapter_id,
      difficulty: q.difficulty,
      type: q.type,
      points: q.points,
      correct_option_ids: q.correct_option_ids,
      tags: q.tags,
      visual_prompt: q.visual_prompt,
      image_url: q.image_url || null,
      de: {
        question: q.question_text_de,
        options: q.options_de,
        explanation: q.explanation_de,
      },
      en: {
        question: q.question_text_en || null,
        options: q.options_en || null,
        explanation: q.explanation_en || null,
      },
      tr: {
        question: q.question_text_tr || null,
        options: q.options_tr || null,
        explanation: q.explanation_tr || null,
      },
      ar: {
        question: q.question_text_ar || null,
        options: q.options_ar || null,
        explanation: q.explanation_ar || null,
      },
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo_fragen_${viewLang}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setMessage({ text: `${exportData.length} Fragen als JSON exportiert.`, type: 'success' });
  };

  // ===== JSON Import (full or partial) =====
  const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) throw new Error('JSON muss ein Array sein.');

      let updated = 0;
      let inserted = 0;
      let errors = 0;

      for (const item of data) {
        try {
          const row: any = {};
          // Map fields
          if (item.id) row.id = item.id;
          else row.id = crypto.randomUUID();
          if (item.chapter_id) row.chapter_id = item.chapter_id;
          if (item.difficulty) row.difficulty = item.difficulty;
          if (item.type) row.type = item.type;
          if (item.points) row.points = item.points;
          if (item.correct_option_ids) row.correct_option_ids = item.correct_option_ids;
          if (item.tags) row.tags = item.tags;
          if (item.visual_prompt) row.visual_prompt = item.visual_prompt;

          // Language fields
          if (item.de) {
            if (item.de.question) row.question_text_de = item.de.question;
            if (item.de.options) row.options_de = item.de.options;
            if (item.de.explanation) row.explanation_de = item.de.explanation;
          }
          if (item.en) {
            if (item.en.question) row.question_text_en = item.en.question;
            if (item.en.options) row.options_en = item.en.options;
            if (item.en.explanation) row.explanation_en = item.en.explanation;
          }
          if (item.tr) {
            if (item.tr.question) row.question_text_tr = item.tr.question;
            if (item.tr.options) row.options_tr = item.tr.options;
            if (item.tr.explanation) row.explanation_tr = item.tr.explanation;
          }
          if (item.ar) {
            if (item.ar.question) row.question_text_ar = item.ar.question;
            if (item.ar.options) row.options_ar = item.ar.options;
            if (item.ar.explanation) row.explanation_ar = item.ar.explanation;
          }

          // Check if question exists
          const exists = questions.some((q) => q.id === row.id);
          const { error } = await adminDb.from('questions').upsert(row, { onConflict: 'id' });
          if (error) throw error;
          if (exists) updated++;
          else inserted++;
        } catch (itemErr: any) {
          console.error('Import item error:', itemErr);
          errors++;
        }
      }

      setMessage({
        text: `Import fertig: ${inserted} neu, ${updated} aktualisiert${errors > 0 ? `, ${errors} Fehler` : ''}.`,
        type: 'success',
      });
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Import-Fehler: ${err.message}`, type: 'error' });
    }
    e.target.value = '';
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split('\n').filter((l) => l.trim());
    if (lines.length < 2) {
      setMessage({ text: 'CSV enthält keine Daten.', type: 'error' });
      return;
    }

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const imported: any[] = [];
    let errors = 0;

    for (let i = 1; i < lines.length; i++) {
      try {
        const cols =
          lines[i]
            .match(/(?:"([^"]*(?:""[^"]*)*)")|([^,]+)/g)
            ?.map((c) => c.replace(/^"|"$/g, '').replace(/""/g, '"').trim()) || [];
        const chapterIdx =
          headers.indexOf('kapitel') !== -1
            ? headers.indexOf('kapitel')
            : headers.indexOf('chapter');
        const diffIdx =
          headers.indexOf('schwierigkeit') !== -1
            ? headers.indexOf('schwierigkeit')
            : headers.indexOf('difficulty');
        const typeIdx =
          headers.indexOf('typ') !== -1 ? headers.indexOf('typ') : headers.indexOf('type');
        const textIdx =
          headers.indexOf('fragetext') !== -1
            ? headers.indexOf('fragetext')
            : headers.indexOf('question');
        const pointsIdx =
          headers.indexOf('punkte') !== -1 ? headers.indexOf('punkte') : headers.indexOf('points');
        const explanationIdx =
          headers.indexOf('erklaerung') !== -1
            ? headers.indexOf('erklaerung')
            : headers.indexOf('explanation');

        if (textIdx === -1 || !cols[textIdx]) {
          errors++;
          continue;
        }

        imported.push({
          id: crypto.randomUUID(),
          chapter_id: parseInt(cols[chapterIdx] || '1') || 1,
          difficulty: cols[diffIdx] || 'Mittel',
          type: cols[typeIdx] || 'Single Choice',
          points: parseInt(cols[pointsIdx] || '3') || 3,
          question_text_de: cols[textIdx],
          explanation_de: cols[explanationIdx] || '',
          options_de: [
            { id: 'A', text: 'Option A' },
            { id: 'B', text: 'Option B' },
            { id: 'C', text: 'Option C' },
            { id: 'D', text: 'Option D' },
          ],
          correct_option_ids: ['A'],
          tags: [],
          image_placeholder: null,
          visual_prompt: null,
        });
      } catch {
        errors++;
      }
    }

    if (imported.length === 0) {
      setMessage({ text: 'Keine Fragen importiert. Pruefe das CSV-Format.', type: 'error' });
      return;
    }

    try {
      const { error } = await adminDb.from('questions').insert(imported);
      if (error) throw error;
      await adminDb
        .from('audit_logs')
        .insert({
          action: 'csv_import',
          resource_type: 'questions',
          admin_email: 'admin',
          details: { imported: imported.length, errors },
        });
      setMessage({
        text: `${imported.length} Fragen importiert${errors > 0 ? `, ${errors} Fehler uebersprungen` : ''}.`,
        type: 'success',
      });
      await loadQuestions();
    } catch (err: any) {
      setMessage({ text: `Import-Fehler: ${err.message}`, type: 'error' });
    }
    e.target.value = '';
  };

  // Stats
  const totalQuestions = questions.length;
  const withImage = questions.filter((q) => q.image_placeholder).length;
  const byChapter = CHAPTERS.map((ch) => ({
    chapter: ch,
    count: questions.filter((q) => q.chapter_id === ch).length,
  }));

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <HelpCircle size={24} /> Fragenmanagement
          </h1>
        </div>
        <div className="grid-cols-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="kpi-card">
              <div className="skeleton" style={{ width: 100, height: 14 }} />
              <div className="skeleton" style={{ width: 60, height: 28, marginTop: 8 }} />
            </div>
          ))}
        </div>
        <div className="table-container">
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <HelpCircle size={24} /> Fragenmanagement
        </h1>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {/* Language switcher */}
          <div
            className="flex gap-1 items-center"
            style={{ borderRight: '1px solid var(--border)', paddingRight: 10, marginRight: 4 }}
          >
            <Globe size={14} style={{ color: 'var(--text-muted)' }} />
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                className={`btn btn-sm ${viewLang === lang.code ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setViewLang(lang.code)}
                title={lang.label}
                style={{ minWidth: 36, padding: '4px 8px' }}
              >
                {lang.flag}
              </button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAiGenerator(true)}>
            <Sparkles size={14} /> KI Generieren
          </button>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            <Upload size={14} /> JSON Import
            <input
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImportJSON}
            />
          </label>
          <button className="btn btn-secondary btn-sm" onClick={handleExportJSON}>
            <FileJson size={14} /> JSON Export
          </button>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            <Upload size={14} /> CSV
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportCSV}
            />
          </label>
          <button className="btn btn-secondary btn-sm" onClick={handleExportCSV}>
            <Download size={14} /> CSV
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadQuestions}>
            <RefreshCw size={14} /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`alert alert-${message.type === 'success' ? 'success' : message.type === 'error' ? 'danger' : 'info'} mb-4`}
        >
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span>{message.text}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid-cols-4 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Fragen gesamt</span>
          <span className="kpi-value">{totalQuestions}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Mit Bild</span>
          <span className="kpi-value">{withImage}</span>
          <span className="text-xs text-muted">
            {totalQuestions > 0 ? Math.round((withImage / totalQuestions) * 100) : 0}%
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Ohne Bild</span>
          <span className="kpi-value">{totalQuestions - withImage}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Kapitel abgedeckt</span>
          <span className="kpi-value">
            {byChapter.filter((c) => c.count > 0).length} / {CHAPTER_IDS.length}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-toolbar" style={{ flexWrap: 'wrap' }}>
          <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: 220 }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }}
              />
              <input
                className="input input-search"
                placeholder="Fragetext suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="input"
              style={{ width: 140 }}
              value={filterChapter}
              onChange={(e) => setFilterChapter(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Alle Kapitel</option>
              {CHAPTERS.map((ch) => (
                <option key={ch} value={ch}>
                  Kapitel {ch} ({byChapter.find((c) => c.chapter === ch)?.count || 0})
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 130 }}
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
            >
              <option value="">Schwierigkeit</option>
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 150 }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Fragentyp</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="input"
              style={{ width: 120 }}
              value={filterHasImage}
              onChange={(e) => setFilterHasImage(e.target.value as any)}
            >
              <option value="">Bild</option>
              <option value="yes">Mit Bild</option>
              <option value="no">Ohne Bild</option>
            </select>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-muted">{filtered.length} Ergebnisse</span>
            {selectedIds.size > 0 && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleDelete(Array.from(selectedIds))}
              >
                <Trash2 size={14} /> {selectedIds.size} löschen
              </button>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filtered.length && filtered.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('chapter_id')} style={{ width: 80 }}>
                  Kap. <SortIcon col="chapter_id" />
                </th>
                <th style={{ width: 70 }}>#</th>
                <th onClick={() => handleSort('question_text_de')}>
                  Fragetext <SortIcon col="question_text_de" />
                </th>
                <th onClick={() => handleSort('difficulty')} style={{ width: 100 }}>
                  Schwierigkeit <SortIcon col="difficulty" />
                </th>
                <th onClick={() => handleSort('type')} style={{ width: 130 }}>
                  Typ <SortIcon col="type" />
                </th>
                <th onClick={() => handleSort('points')} style={{ width: 60 }}>
                  Pkt <SortIcon col="points" />
                </th>
                <th style={{ width: 60 }}>Bild</th>
                <th style={{ width: 80 }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                  >
                    Keine Fragen gefunden.
                  </td>
                </tr>
              ) : (
                filtered.map((q) => (
                  <tr key={q.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(q.id)}
                        onChange={() => toggleSelect(q.id)}
                      />
                    </td>
                    <td>
                      <span className="badge badge-neutral">{q.chapter_id}</span>
                    </td>
                    <td>
                      <span className="text-xs text-muted">
                        {chapterQuestionNumbers[q.id]?.num || '—'}/
                        {chapterQuestionNumbers[q.id]?.total || '—'}
                      </span>
                    </td>
                    <td>
                      <div
                        style={{ maxWidth: 400 }}
                        className="truncate"
                        title={(q as any)[`question_text_${viewLang}`] || q.question_text_de}
                      >
                        {(q as any)[`question_text_${viewLang}`] || q.question_text_de}
                        {viewLang !== 'de' && !(q as any)[`question_text_${viewLang}`] && (
                          <span
                            className="badge badge-warning"
                            style={{ marginLeft: 6, fontSize: 9 }}
                          >
                            DE
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge ${q.difficulty === 'Leicht' ? 'badge-success' : q.difficulty === 'Mittel' ? 'badge-warning' : 'badge-danger'}`}
                      >
                        {q.difficulty}
                      </span>
                    </td>
                    <td className="text-sm">{q.type}</td>
                    <td className="text-sm">{q.points}</td>
                    <td>
                      {q.image_placeholder ? (
                        <span className="badge badge-success" title="Hat Bild">
                          <ImageIcon size={12} />
                        </span>
                      ) : (
                        <span className="badge badge-neutral" title="Kein Bild">
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Bearbeiten"
                          onClick={() => setEditingQuestion({ ...q })}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Löschen"
                          onClick={() => handleDelete([q.id])}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingQuestion && (
        <QuestionEditor
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onClose={() => setEditingQuestion(null)}
          saving={saving}
        />
      )}

      {/* AI Generator Modal */}
      {showAiGenerator && (
        <AiQuestionGenerator
          onClose={() => setShowAiGenerator(false)}
          onGenerated={() => {
            setShowAiGenerator(false);
            loadQuestions();
          }}
          setMessage={setMessage}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ---- Question Editor Modal (Multilingual) ----

const QuestionEditor: React.FC<{
  question: QuestionRow;
  onSave: (q: QuestionRow) => void;
  onClose: () => void;
  saving: boolean;
}> = ({ question, onSave, onClose, saving }) => {
  const [q, setQ] = useState<QuestionRow>({ ...question });
  const [editLang, setEditLang] = useState('de');

  const textKey = `question_text_${editLang}` as keyof QuestionRow;
  const optKey = `options_${editLang}` as keyof QuestionRow;
  const expKey = `explanation_${editLang}` as keyof QuestionRow;

  const currentText = (q[textKey] as string) || '';
  const currentOptions = (q[optKey] as { id: string; text: string }[]) || q.options_de || [];
  const currentExplanation = (q[expKey] as string) || '';

  const updateText = (val: string) => setQ({ ...q, [textKey]: val });
  const updateExplanation = (val: string) => setQ({ ...q, [expKey]: val });
  const updateOption = (index: number, text: string) => {
    const opts = [...currentOptions];
    opts[index] = { ...opts[index], text };
    setQ({ ...q, [optKey]: opts });
  };

  const toggleCorrect = (optId: string) => {
    const ids = q.correct_option_ids.includes(optId)
      ? q.correct_option_ids.filter((id) => id !== optId)
      : [...q.correct_option_ids, optId];
    setQ({ ...q, correct_option_ids: ids });
  };

  const hasTranslation = (lang: string) => !!(q as any)[`question_text_${lang}`];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 760,
          padding: 32,
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2>Frage bearbeiten</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Language Tabs */}
        <div
          className="flex gap-1 mb-5"
          style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8 }}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`btn btn-sm ${editLang === lang.code ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setEditLang(lang.code)}
              style={{ position: 'relative' }}
            >
              {lang.flag} {lang.code.toUpperCase()}
              {lang.code !== 'de' && (
                <span
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: hasTranslation(lang.code)
                      ? 'var(--success, #22c55e)'
                      : 'var(--danger, #ef4444)',
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Chapter + Difficulty + Type (shared across all languages) */}
        <div className="grid-cols-3 mb-4">
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Kapitel
            </label>
            <select
              className="input"
              value={q.chapter_id}
              onChange={(e) => setQ({ ...q, chapter_id: Number(e.target.value) })}
            >
              {CHAPTERS.map((ch) => (
                <option key={ch} value={ch}>
                  Kapitel {ch}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Schwierigkeit
            </label>
            <select
              className="input"
              value={q.difficulty}
              onChange={(e) => setQ({ ...q, difficulty: e.target.value })}
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Typ
            </label>
            <select
              className="input"
              value={q.type}
              onChange={(e) => setQ({ ...q, type: e.target.value })}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Points */}
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Fehlerpunkte
          </label>
          <input
            className="input"
            type="number"
            min={2}
            max={5}
            value={q.points}
            onChange={(e) => setQ({ ...q, points: Number(e.target.value) })}
            style={{ width: 80 }}
          />
        </div>

        {/* Question Text (language-dependent) */}
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Fragetext ({editLang.toUpperCase()})
            {editLang !== 'de' && !currentText && (
              <span className="text-xs" style={{ color: 'var(--danger)', marginLeft: 8 }}>
                Noch nicht übersetzt
              </span>
            )}
          </label>
          <textarea
            className="input"
            rows={3}
            value={currentText}
            onChange={(e) => updateText(e.target.value)}
            placeholder={
              editLang !== 'de' ? `Übersetzung eingeben... (DE: ${q.question_text_de || ''})` : ''
            }
          />
          {editLang !== 'de' && q.question_text_de && (
            <div
              className="text-xs text-muted mt-1"
              style={{ background: 'var(--bg-secondary)', padding: '6px 10px', borderRadius: 6 }}
            >
              🇩🇪 {q.question_text_de}
            </div>
          )}
        </div>

        {/* Options (language-dependent) */}
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Antwortoptionen ({editLang.toUpperCase()})
          </label>
          <div className="flex flex-col gap-2">
            {currentOptions.map((opt, i) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={q.correct_option_ids.includes(opt.id)}
                  onChange={() => toggleCorrect(opt.id)}
                  title="Korrekte Antwort"
                  disabled={editLang !== 'de'}
                />
                <span className="text-xs" style={{ fontWeight: 600, width: 20 }}>
                  {opt.id})
                </span>
                <input
                  className="input"
                  value={opt.text}
                  onChange={(e) => updateOption(i, e.target.value)}
                  placeholder={editLang !== 'de' ? q.options_de?.[i]?.text || '' : ''}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted mt-2">
            {editLang === 'de'
              ? 'Checkbox = korrekte Antwort'
              : 'Korrekte Antworten werden nur in DE gesetzt'}
          </p>
        </div>

        {/* Explanation (language-dependent) */}
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Erklärung ({editLang.toUpperCase()})
          </label>
          <textarea
            className="input"
            rows={2}
            value={currentExplanation}
            onChange={(e) => updateExplanation(e.target.value)}
            placeholder={
              editLang !== 'de' ? `Übersetzung eingeben... (DE: ${q.explanation_de || ''})` : ''
            }
          />
        </div>

        {/* Visual Prompt (shared) */}
        <div className="mb-6">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Bild-Prompt (für KI-Generierung)
          </label>
          <input
            className="input"
            value={q.visual_prompt || ''}
            onChange={(e) => setQ({ ...q, visual_prompt: e.target.value })}
            placeholder="z.B. Kreuzung mit Vorfahrtsschild rechts, Fahrerperspektive..."
          />
        </div>

        {/* Image Preview */}
        {(q.image_url || q.image_placeholder) && (
          <div className="mb-6">
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Aktuelles Bild
            </label>
            <img
              src={
                q.image_url ||
                (q.image_placeholder?.startsWith('data:') || q.image_placeholder?.startsWith('http')
                  ? q.image_placeholder
                  : '')
              }
              alt="Fragebild"
              style={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 8,
                border: '1px solid var(--border)',
              }}
            />
          </div>
        )}

        {/* Translation Status */}
        <div
          className="mb-6"
          style={{ padding: 12, borderRadius: 8, background: 'var(--bg-secondary)' }}
        >
          <div className="text-xs" style={{ fontWeight: 600, marginBottom: 6 }}>
            Übersetzungsstatus
          </div>
          <div className="flex gap-3">
            {LANGUAGES.map((lang) => (
              <div key={lang.code} className="flex items-center gap-1">
                <span>{lang.flag}</span>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: hasTranslation(lang.code)
                      ? 'var(--success, #22c55e)'
                      : 'var(--danger, #ef4444)',
                  }}
                />
                <span className="text-xs text-muted">{lang.code.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <button className="btn btn-secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={() => onSave(q)} disabled={saving}>
            {saving ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Save size={16} />
            )}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- AI Question Generator Modal ----

const CHAPTER_NAMES = CHAPTER_TITLES_DE;

const PROMPT_TEMPLATES = [
  'Erstelle prüfungsähnliche Fragen mit realistischen Szenarien.',
  'Erstelle Fragen zu Verkehrsschildern und deren Bedeutung.',
  'Erstelle Fragen mit Situationsbeschreibungen an Kreuzungen.',
  'Erstelle schwierige Fragen mit Fangoptionen.',
  'Erstelle Fragen speziell für Fahranfänger mit einfacher Sprache.',
];

const AiQuestionGenerator: React.FC<{
  onClose: () => void;
  onGenerated: () => void;
  setMessage: (msg: { text: string; type: 'success' | 'error' | 'info' }) => void;
}> = ({ onClose, onGenerated, setMessage }) => {
  const [chapter, setChapter] = useState(1);
  const [difficulty, setDifficulty] = useState('Mittel');
  const [qType, setQType] = useState('Single Choice');
  const [quantity, setQuantity] = useState(5);
  const [prompt, setPrompt] = useState(PROMPT_TEMPLATES[0]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [preview, setPreview] = useState<any[]>([]);

  const handleGenerate = async () => {
    setGenerating(true);
    setProgress(`KI generiert ${quantity} Fragen...`);
    setPreview([]);

    // The prompt lives on the server now. This used to call Gemini directly
    // from the browser with a VITE_GEMINI_API_KEY, which would have shipped an
    // API key to every admin client (and never worked, because the variable was
    // not set anywhere).
    try {
      const response = await adminFetch('/api/admin/questions/generate', {
        method: 'POST',
        body: JSON.stringify({ chapter, difficulty, qType, quantity, prompt }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || `API Fehler: ${response.status}`);

      const generated = result.questions;
      if (!Array.isArray(generated) || generated.length === 0) {
        throw new Error('Keine Fragen generiert.');
      }

      setProgress(`${generated.length} Fragen generiert. Vorschau...`);
      setPreview(generated);
    } catch (err: any) {
      setMessage({ text: `Generierung fehlgeschlagen: ${err.message}`, type: 'error' });
      setGenerating(false);
    }
  };

  const handleSaveAll = async () => {
    setProgress('Speichere in Datenbank...');
    try {
      const rows = preview.map((q) => ({
        id: crypto.randomUUID(),
        chapter_id: chapter,
        difficulty,
        type: qType,
        points: difficulty === 'Leicht' ? 2 : difficulty === 'Mittel' ? 3 : 5,
        question_text_de: q.question,
        options_de: q.options,
        correct_option_ids: q.correct,
        explanation_de: q.explanation,
        tags: [`ki-generiert`, `kap-${chapter}`],
        image_placeholder: null,
        visual_prompt: q.visual_prompt || null,
      }));

      const { error } = await adminDb.from('questions').insert(rows);
      if (error) throw error;

      await adminDb.from('audit_logs').insert({
        action: 'ai_question_generation',
        resource_type: 'questions',
        admin_email: 'admin',
        details: { count: rows.length, chapter, difficulty, prompt: prompt.substring(0, 100) },
      });

      setMessage({
        text: `${rows.length} KI-Fragen gespeichert für Kapitel ${chapter}.`,
        type: 'success',
      });
      onGenerated();
    } catch (err: any) {
      setMessage({ text: `Speicherfehler: ${err.message}`, type: 'error' });
    }
    setGenerating(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '30px 20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: 800,
          padding: 32,
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2>
            <Sparkles size={20} /> KI-Fragengenerator
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Configuration */}
        <div className="grid-cols-4 mb-4" style={{ gap: 12 }}>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Kapitel
            </label>
            <select
              className="input"
              value={chapter}
              onChange={(e) => setChapter(Number(e.target.value))}
            >
              {Object.entries(CHAPTER_NAMES).map(([k, v]) => (
                <option key={k} value={k}>
                  Kap. {k}: {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Schwierigkeit
            </label>
            <select
              className="input"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
            >
              <option>Leicht</option>
              <option>Mittel</option>
              <option>Schwer</option>
            </select>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Typ
            </label>
            <select className="input" value={qType} onChange={(e) => setQType(e.target.value)}>
              <option>Single Choice</option>
              <option>Multiple Choice</option>
              <option>Situation</option>
            </select>
          </div>
          <div>
            <label
              className="text-xs"
              style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
            >
              Anzahl
            </label>
            <input
              className="input"
              type="number"
              min={1}
              max={20}
              value={quantity}
              onChange={(e) => setQuantity(Math.min(20, Math.max(1, Number(e.target.value))))}
            />
          </div>
        </div>

        {/* Prompt */}
        <div className="mb-4">
          <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            Prompt / Anweisung
          </label>
          <textarea
            className="input"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Beschreibe, welche Art von Fragen generiert werden sollen..."
          />
          <div className="flex gap-2 mt-2 flex-wrap">
            {PROMPT_TEMPLATES.map((t, i) => (
              <button
                key={i}
                className={`btn btn-sm ${prompt === t ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPrompt(t)}
                style={{ fontSize: 11, padding: '2px 8px' }}
              >
                {t.substring(0, 40)}...
              </button>
            ))}
          </div>
        </div>

        {/* Progress */}
        {progress && (
          <div className="alert alert-info mb-4">
            {generating && !preview.length && (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            )}
            <span>{progress}</span>
          </div>
        )}

        {/* Preview */}
        {preview.length > 0 && (
          <div
            className="mb-4"
            style={{
              maxHeight: 300,
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
            }}
          >
            <h4 className="mb-2 text-sm" style={{ fontWeight: 600 }}>
              Vorschau: {preview.length} Fragen
            </h4>
            {preview.map((q: any, i: number) => (
              <div
                key={i}
                style={{ marginBottom: 12, padding: 8, background: 'var(--bg)', borderRadius: 6 }}
              >
                <div className="text-sm" style={{ fontWeight: 600 }}>
                  {i + 1}. {q.question}
                </div>
                <div className="text-xs text-muted mt-1">
                  {q.options?.map((o: any) => (
                    <span
                      key={o.id}
                      style={{
                        marginRight: 12,
                        color: q.correct?.includes(o.id) ? 'var(--success)' : undefined,
                        fontWeight: q.correct?.includes(o.id) ? 700 : 400,
                      }}
                    >
                      {o.id}) {o.text}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between">
          <button className="btn btn-secondary" onClick={onClose}>
            Abbrechen
          </button>
          <div className="flex gap-2">
            {preview.length === 0 ? (
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Sparkles size={16} />
                )}
                {generating ? 'Generiert...' : `${quantity} Fragen generieren`}
              </button>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setPreview([]);
                    setProgress('');
                    setGenerating(false);
                  }}
                >
                  Neu generieren
                </button>
                <button className="btn btn-primary" onClick={handleSaveAll}>
                  <Save size={16} /> Alle {preview.length} speichern
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
